import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, ViewChild } from '@angular/core';
import { first } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { ButtonsTranslations } from '~shared/components/buttons/buttons-translations.service';
import { SpinnerService } from '~shared/components/buttons/spinner/spinner.service';
import { FormLabelTranslationService } from '~shared/components/form-fields/translations/form-label-translations.service';
import { NotificationType } from '~core/enum';
import { Club, ImportCsvSample } from '~core/models';
import { DefaultNotificationService, ImportsService } from '~core/services';
import { ImportCustomerTranslationsService } from '~shared/translations';

/**
 * Component that displays the editable data inside of a workspace sidebar and communicates with the Club API
 */
@Component({
  selector: 'app-upload-import',
  templateUrl: './upload-import.component.html'
})
export class UploadImportComponent implements OnChanges, OnDestroy {
  @Input() public isOpen: boolean;
  @Input() public selectedClub: Club;
  @Input() public dataType: string;

  @Output() public emitIsOpen: EventEmitter<any> = new EventEmitter<any>(null);

  @ViewChild('fileInput') fileInput: ElementRef;

  public spinnerName: 'importData';
  public fileToUpload: any = null;
  public fileLabel: string = null;
  public csvImportExample: ImportCsvSample = new ImportCsvSample();

  private allowedFileTypes: Array<string> = [
    'text/csv',
    'text/comma-separated-values',
    'application/csv',
    'application/excel',
    'application/vnd.ms-excel',
    'application/vnd.msexcel'
  ];

  constructor(
    public flTranslations: FormLabelTranslationService,
    public buttonsTranslations: ButtonsTranslations,
    private notificationService: DefaultNotificationService,
    private importCustomerTranslations: ImportCustomerTranslationsService,
    private importsService: ImportsService,
    private spinnerService: SpinnerService,
  ) { }

  public ngOnChanges(): void {
    if (this.isOpen) {
      this.fileInput.nativeElement.click();
    }
  }

  public ngOnDestroy(): void { }

  /**
   * Set the status of the sidebar
   */
  public setIsOpen(status: boolean): void {
    this.emitIsOpen.emit(status);
  }

  /**
   * Verifies that a file is of type .csv
   */
  public onFileChange(eventTarget: any): void {
    if (eventTarget.files && eventTarget.files.length > 0) {
      const file = eventTarget.files[0];

      if (this.isAllowedType(file)) {
        this.fileLabel = file.name;
        this.fileToUpload = file;

      } else {
        eventTarget.value = '';
        this.notificationService.showNotification({
          type: NotificationType.ALERT,
          message: this.importCustomerTranslations.i18nErrorImportFileType
        });
        this.fileToUpload = null;
      }
    }
  }

  /**
   * Post the data from the form.
   *
   * @returns void
   */
  public onSubmit(): void {
    const formData: FormData = new FormData();
    formData.append('file', this.fileToUpload);
    this.spinnerService.show(this.spinnerName);
    this.importsService.postImportMedia(formData).pipe(first())
      .subscribe({
        next: data => {
          const importRequest = { club: this.selectedClub['@id'], media: data['@id'], type: this.dataType };
          this.importsService.postImportRequest(importRequest).pipe(first())
            .subscribe({
              next: req => {
                this.setIsOpen(false);
                this.spinnerService.hide(this.spinnerName);
                this.notificationService.showNotification({
                  type: NotificationType.SUCCESS,
                  message: this.importCustomerTranslations.i18nSuccessImport
                });
              },
              error: (error: HttpErrorResponse) => {
                if (error.error['hydra:description'] === 'INVALID_HEADERS_PROVIDED') {
                  this.notificationService.showNotification({
                    type: NotificationType.ALERT,
                    message: this.importCustomerTranslations.i18nErrorWrongHeaders
                  });
                }
                this.spinnerService.hide(this.spinnerName);
              }
            });
        },
        error: (error: HttpErrorResponse) => {
          this.spinnerService.hide(this.spinnerName);
          this.notificationService.showNotification({
            type: NotificationType.ALERT,
            message: this.importCustomerTranslations.i18nErrorImport
          });
        }
      });
  }

  /**
   * Checks if the provided file type is valid.
   *
   * @param file The file type from the uploaded file.
   * @returns boolean
   */
  private isAllowedType(file: File): boolean {
    return this.allowedFileTypes.indexOf(file.type) > -1 || file.name.indexOf('.csv') > -1;
  }

}
